import { UserType } from "@app/enum/UserType";
import { IGetRatingsOptions, NPSService } from "@app/main/components/nps/nps.service";
import { DateService } from "@app/services/date.service";
import { DisplayService, IDialogOptionsJson } from "@app/services/display.service";
import { TranslationService } from "@app/services/translation.service";
import { VersionService } from "@app/services/version.service";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser, UserPlatform } from "@interfaces/IUser";
import { AccountService } from "@services/account.service";
import { ConstantsService } from "@services/constantsService";
import { CurrentUserService } from "@services/currentUser.service";
import { LoginService } from "@services/login.service.ajs";
import { SessionService } from "@services/session.service";
import { StateDeclaration, StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import { INavigationDeclarationObject, INavigationItem, SideNavigationService } from "src/app/main/navigation/sideNavigation.service";
import "./moduleNavigation.scss";

class ComponentController implements angular.IComponentController {

    UserPlatform = UserPlatform;
    UserType = UserType;

    navigationItems: INavigationItem[] = [];
    navObject: INavigationDeclarationObject;
    eventListeners = new Map<string, { (): void; }>();

    currentUser: IUser;
    accountName: string;

    isMobile = false;
    showNoAuthorizationWarning: boolean;

    collapseNav = localStorage.getItem('collapseNav') == "true";
    npsRaterIsVisible = false;

    appVersion: string;
    betaMessage: string;

    static $inject = ["$element", "$interval", "$rootScope", "$state", "sideNavigationService", "constantsService", "sessionService", "accountService", "loginService", "npsService", "dateService", "displayService", "versionService", "translationService"];

    constructor(
        private readonly $element: JQuery,
        private readonly $interval: angular.IIntervalService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $state: StateService,
        private readonly sideNavigationService: SideNavigationService,
        private readonly constantsService: ConstantsService,
        private readonly sessionService: SessionService,
        private readonly accountService: AccountService,
        private readonly loginService: LoginService,
        private readonly npsService: NPSService,
        private readonly dateService: DateService,
        private readonly displayService: DisplayService,
        private readonly versionService: VersionService,
        private readonly translationService: TranslationService) {
        this.currentUser = angular.copy($rootScope.currentUser);
        this.toggleNpsRaterVisibility();
    }

    $onInit() {
        this.init();
        this.updateAccountInfo();
    }

    $onDestroy() {
        this.removeEventListeners();
        this.removeAllListeners();
    }

    toggleNavCollapsedState() {
        this.collapseNav = !this.collapseNav;

        localStorage.setItem('collapseNav', this.collapseNav.toString());

        this.setCollapseNavClass();
    }

    parentNavItemClicked(navItem: INavigationItem, $event?: MouseEvent) {

        this.sideNavigationService.removeNavItemNewStatus(navItem);

        if (!navItem.subNavItems) {
            return true;
        }

        navItem.expanded = !navItem.expanded;

        $event?.preventDefault();

        this.closeOtherNavParents(navItem);
        this.addRemoveListeners(navItem);

        return false;
    }

    navItemClicked(navItem: INavigationItem): void {
        this.setHasNewSubNavItemStatus(navItem);

        if (!navItem || this.isMobile) {
            return;
        }

        this.closeOtherNavParents(navItem);
    }

    goToFoundation() {
        this.loginService.switchToInternal();
    }

    gotoSearch() {
        this.$state.go("app.sitesearch");
    }

    logout() {
        this.sessionService.logout();
    }

    switchAccount($event: MouseEvent) {
        const dialogHTML = `<account-switcher on-account-switched="$ctrl.onAccountSwitched($event);"></account-switcher>`;

        const dialogOptions: IDialogOptionsJson = {
            dialogId: "switch-account",
            title: this.translationService.t("Navigation.SwitchAccount"),
            dialogHTML: dialogHTML,
            bindings: {
                onAccountSwitched: (accountId: number) => { return accountId; }
            },
            event: $event
        };

        return this.displayService.showDialog(dialogOptions);
    }

    showSettings(event: MouseEvent) {
        this.accountService.showMyPreferencesDialog(event);
    }

    showPasswordEditor($event: MouseEvent) {
        this.accountService.showPasswordEditor($event);
    }

    showNPSRater() {
        this.npsService.showNPSRater().then(npsEntry => {

            if (npsEntry) {
                this.npsRaterIsVisible = false;
                this.$rootScope.$applyAsync();
            }
        });
    }

    // ---

    private init() {
        this.isMobile = this.constantsService.getIsMobile();

        if (this.isMobile) {
            this.$element.addClass("mobile-mode");
        }

        // If we are mobile, then default to collapsed
        this.collapseNav = this.isMobile;
        if (this.isMobile) {
            this.setCollapseNavClass();
        }

        this.createNavigationForUser();

        this.addEventListener("$stateChangeSuccess", this.$rootScope.$on("$stateChangeSuccess", (event, toState: StateDeclaration, toParams: StateDeclaration, fromState) => {
            if (this.isMobile && !this.collapseNav) {
                this.toggleNavCollapsedState();
            }

            this.findAndExpandParentNav(this.navigationItems, toState);
        }));

        this.updateVersionNumber();
        this.updateBetaMessage();

        this.$rootScope.$on(AccountService.AccountFeaturesUpdatedEvent, (event: angular.IAngularEvent) => this.createNavigationForUser());
        this.$rootScope.$on(CurrentUserService.CurrentUserFeaturesUpdatedEvent, (event: angular.IAngularEvent) => this.createNavigationForUser());
    }

    private removeAllListeners() {
        this.navObject?.navigationItems.forEach(navItem => {
            this.removeListeners(navItem);
        });
    }

    private removeListeners(navItem: INavigationItem) {

        this.$interval.cancel(navItem.listener);

        navItem.subNavItems?.forEach(subItem => {
            this.removeListeners(subItem);
        })
    }

    private addRemoveListeners(navItem: INavigationItem) {
        this.removeListeners(navItem);
        this.createListenersForNavItem(navItem);
    }

    private findAndExpandParentNav(navigationItems: INavigationItem[], toState: StateDeclaration) {
        navigationItems.forEach(navItem => {
            if (!navItem.subNavItems) {
                return;
            }

            navItem.subNavItems.forEach(subNavItem => {

                if (subNavItem.uiSref == toState.name || toState.name.indexOf(subNavItem.uiSref) >= 0) {
                    navItem.expanded = true;

                    this.addRemoveListeners(navItem);
                }
            });
        });
    }

    private createListenersForNavItem(navItem: INavigationItem) {
        if (navItem.expanded && navItem.createListenerCallback) {
            navItem.listener = navItem.createListenerCallback(navItem);
        }

        if (navItem.subNavItems?.length) {
            navItem.subNavItems.forEach(subNavItem => {
                if (subNavItem.createListenerCallback) {
                    subNavItem.listener = subNavItem.createListenerCallback(subNavItem);
                }
            });
        }
    }

    private addEventListener(eventName: string, func: { (): void }): void {
        if (this.eventListeners.has(eventName)) {
            return;
        }

        this.eventListeners.set(eventName, func);
    }

    private removeEventListeners(): void {
        this.eventListeners.forEach(func => func());
    }

    private createNavigationForUser(): void {
        this.removeAllListeners();

            this.sideNavigationService.createNavigationForUser().then(nav => {
                this.navObject = nav;
                this.navigationItems = nav.navigationItems;
    
                this.showNoAuthorizationWarning = !this.navigationItems.filter(item => item.uiSref !== "app.components_diagnostics")?.length;
    
                // If we have only a single parent, we should expand it as it looks nicer
                const parents = nav.navigationItems.filter(n => n.subNavItems && n.subNavItems.length != 0);
    
                if (parents.length == 1) {
                    parents[0].expanded = true;
                }
    
                this.findAndExpandParentNav(nav.navigationItems, this.$state.current);
            });
    }

    private setCollapseNavClass() {
        this.$element.toggleClass("collapse-nav");
    }

    private closeOtherNavParents(navItem: INavigationItem): void {
        this.navigationItems?.forEach(navigationItem => {
            if (navigationItem === navItem) {
                return;
            }

            navigationItem.expanded = false;
            navigationItem?.subNavItems?.forEach(subNavItem => {
                subNavItem.expanded = false;
            });

            this.removeListeners(navigationItem);
        });
    }

    private toggleNpsRaterVisibility() {
        if (!this.currentUser?.CurrentAccountUserMapping || this.currentUser?.Type == UserType.InternalUser) {
            return;
        }

        var options: IGetRatingsOptions = {
            platformId: "AST-Verilocation",
            customerId: this.currentUser.CurrentAccountUserMapping.AccountId.toString(),
            userId: this.currentUser.Id.toString(),
        };

        this.npsService.getLatestRating(options).subscribe(npsEntry => {

            if (!npsEntry) {
                this.npsRaterIsVisible = true;
            }
            else {
                var daysSinceLastRating = this.dateService.getDaysDifference(new Date(npsEntry.DateTime), new Date);
                this.npsRaterIsVisible = daysSinceLastRating >= 14;

                // If its been longer than a month, then show popup box itself
                if (this.npsRaterIsVisible && daysSinceLastRating % 28 == 0) {
                    this.showNPSRater();
                }
            }

            // Dont show on mobile
            if (this.isMobile) {
                this.npsRaterIsVisible = false;
            }
        });
    }

    private updateAccountInfo() {
        if (this.currentUser && this.currentUser.platform == UserPlatform.Foundation) {
            this.accountName = this.currentUser.IsReseller ? "Foundation (AddSecure)" : "Foundation (UK)";

            return;
        }

        this.accountService.getAccountData()
            .then(accountData => {
                this.accountName = accountData.CompanyName;
            });
    }

    private updateVersionNumber() {
        this.versionService
            .getAPIVersion()
            .then(version => {
                this.appVersion = version;

                // In here to allow the UI to catch up with itself before having first stab at the breadcrumbs
                //this.updateBreadcrumbs();
            })
            .catch(angular.noop);
    }

    private updateBetaMessage() {
        this.betaMessage = "";

        if (this.constantsService.getIsProduction()) {
            return;
        }

        if (this.constantsService.getIsDemoSite()) {
            this.betaMessage = "";
        }
        else if (this.constantsService.getIsLocalDevelopment() || this.constantsService.getIsDev()) {
            this.betaMessage = "Dev";
        }
        else {
            this.betaMessage = this.constantsService.getIsFleet() ? "" : "UAT";
        }
    }

    private setHasNewSubNavItemStatus(navItem: INavigationItem): void {
        navItem.hasNewSubItem = navItem.subNavItems?.some(item => item.new);
    }
}

export class ModuleNavigationComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./moduleNavigation.html");
    }
}
